import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  lang: 'en' | 'de' | 'fr' | 'it';
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalPath?: string;
  noindex?: boolean;
  breadcrumbs?: Array<{ name: string; path: string }>;
  // Extra JSON-LD nodes to merge into @graph.
  extraStructuredData?: any[];
}

const seoData = {
    en: {
      title: 'TTStats - Swiss Table Tennis Rankings, ELO & Player Statistics | Switzerland',
      description: 'Track Swiss table tennis players, live ELO ratings, rankings and match statistics. Complete database of all Swiss table tennis players from click-tt.ch with advanced analytics, head-to-head comparisons, and performance tracking.',
      keywords: 'swiss table tennis, table tennis switzerland, swiss table tennis rankings, swiss table tennis players, elo rating table tennis, table tennis elo switzerland, click-tt, stt swiss table tennis, swiss table tennis statistics, table tennis rankings switzerland, swiss table tennis database, table tennis player search switzerland, schweiz tischtennis, suisse tennis de table',
      ogTitle: 'TTStats - Swiss Table Tennis Rankings & ELO Statistics',
      ogDescription: 'Complete Swiss table tennis player database with live ELO ratings, rankings, match history and advanced statistics. Track every player in Switzerland.'
    },
    de: {
      title: 'TTStats - Tischtennis Schweiz Rangliste, ELO & Spieler Statistiken',
      description: 'Verfolge Schweizer Tischtennisspieler, Live ELO-Wertungen, Ranglisten und Spielstatistiken. Vollständige Datenbank aller Schweizer Tischtennisspieler von click-tt.ch mit erweiterten Analysen, direkten Vergleichen und Leistungsverfolgung.',
      keywords: 'tischtennis schweiz, schweizer tischtennis, tischtennis rangliste schweiz, tischtennis spieler schweiz, tischtennis elo, elo rating tischtennis, click-tt schweiz, stt tischtennis, tischtennis statistik schweiz, tischtennis datenbank schweiz, tischtennisspieler suchen schweiz, tischtennis zürich, tischtennis bern, tischtennis ranking, schweizer tischtennisverband',
      ogTitle: 'TTStats - Tischtennis Schweiz Rangliste & ELO Statistiken',
      ogDescription: 'Vollständige Schweizer Tischtennis Spielerdatenbank mit Live ELO-Wertungen, Ranglisten, Spielhistorie und erweiterten Statistiken. Verfolge jeden Spieler in der Schweiz.'
    },
    fr: {
      title: 'TTStats - Tennis de table Suisse Classements, ELO & Statistiques Joueurs',
      description: 'Suivez les joueurs de tennis de table suisses, classements ELO en direct, rangs et statistiques de matchs. Base de données complète de tous les joueurs suisses de tennis de table de click-tt.ch avec analyses avancées, comparaisons directes et suivi des performances.',
      keywords: 'tennis de table suisse, suisse tennis de table, classement tennis de table suisse, joueurs tennis de table suisse, classement elo tennis de table, elo tennis de table suisse, click-tt suisse, stt tennis de table, statistiques tennis de table suisse, base de données tennis de table suisse, recherche joueur tennis de table suisse, tennis de table genève, tennis de table lausanne, fédération suisse tennis de table',
      ogTitle: 'TTStats - Tennis de table Suisse Classements & Statistiques ELO',
      ogDescription: 'Base de données complète des joueurs suisses de tennis de table avec classements ELO en direct, rangs, historique des matchs et statistiques avancées. Suivez chaque joueur en Suisse.'
    },
    it: {
      title: 'TTStats - Ping Pong Svizzera Classifiche, ELO & Statistiche Giocatori',
      description: 'Segui i giocatori di ping pong svizzeri, classifiche ELO in diretta, ranghi e statistiche delle partite. Database completo di tutti i giocatori svizzeri di ping pong da click-tt.ch con analisi avanzate, confronti diretti e monitoraggio delle prestazioni.',
      keywords: 'ping pong svizzera, svizzera ping pong, classifica ping pong svizzera, giocatori ping pong svizzera, classifica elo ping pong, elo ping pong svizzera, click-tt svizzera, stt ping pong, statistiche ping pong svizzera, database ping pong svizzera, ricerca giocatore ping pong svizzera, tennistavolo svizzera, federazione svizzera ping pong',
      ogTitle: 'TTStats - Ping Pong Svizzera Classifiche & Statistiche ELO',
      ogDescription: 'Database completo dei giocatori svizzeri di ping pong con classifiche ELO in diretta, ranghi, storico partite e statistiche avanzate. Segui ogni giocatore in Svizzera.'
    }
  };

const SEOHead: React.FC<SEOHeadProps> = ({
  lang,
  title: titleOverride,
  description: descriptionOverride,
  keywords: keywordsOverride,
  ogTitle: ogTitleOverride,
  ogDescription: ogDescriptionOverride,
  ogImage: ogImageOverride,
  canonicalPath: canonicalPathOverride,
  noindex,
  breadcrumbs,
  extraStructuredData
}) => {
  const location = useLocation();

  useEffect(() => {
    const ensureMeta = (selector: string, create: () => HTMLMetaElement) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      return el;
    };

    const ensureLink = (selector: string, create: () => HTMLLinkElement) => {
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      return el;
    };

    const data = seoData[lang];
    const canonicalPath = canonicalPathOverride || location.pathname;
    const title = titleOverride || data.title;
    const description = descriptionOverride || data.description;
    const keywords = keywordsOverride || data.keywords;
    const ogTitle = ogTitleOverride || data.ogTitle;
    const ogDescription = ogDescriptionOverride || data.ogDescription;
    const ogImage = ogImageOverride || 'https://ttstats.ch/og-image.png';
    const isNoindex = Boolean(noindex);
    const extraGraphNodes = (extraStructuredData || []) as any[];

    // Update title
    document.title = title;

    // Update meta description
    const descriptionMeta = ensureMeta('meta[name="description"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      return m;
    });
    descriptionMeta.setAttribute('content', description);

    // Update keywords
    const keywordsMeta = ensureMeta('meta[name="keywords"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'keywords');
      return m;
    });
    keywordsMeta.setAttribute('content', keywords);

    // Update Open Graph tags
    const ogTitleMeta = ensureMeta('meta[property="og:title"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:title');
      return m;
    });
    ogTitleMeta.setAttribute('content', ogTitle);

    const ogDescMeta = ensureMeta('meta[property="og:description"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:description');
      return m;
    });
    ogDescMeta.setAttribute('content', ogDescription);

    const ogImageMeta = ensureMeta('meta[property="og:image"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:image');
      return m;
    });
    ogImageMeta.setAttribute('content', ogImage);

    // Update og:url
    const ogUrlMeta = ensureMeta('meta[property="og:url"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:url');
      return m;
    });
    ogUrlMeta.setAttribute('content', `https://ttstats.ch${canonicalPath}`);

    // Update Twitter card tags
    const twitterTitleMeta = ensureMeta('meta[name="twitter:title"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'twitter:title');
      return m;
    });
    twitterTitleMeta.setAttribute('content', ogTitle);

    const twitterDescMeta = ensureMeta('meta[name="twitter:description"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'twitter:description');
      return m;
    });
    twitterDescMeta.setAttribute('content', ogDescription);

    const twitterImageMeta = ensureMeta('meta[name="twitter:image"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'twitter:image');
      return m;
    });
    twitterImageMeta.setAttribute('content', ogImage);

    // robots
    const robotsMeta = ensureMeta('meta[name="robots"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      return m;
    });
    robotsMeta.setAttribute('content', isNoindex ? 'noindex, nofollow' : 'index, follow');

    // Update HTML lang attribute
    document.documentElement.lang = lang;

    // Canonical + hreflang alternates
    const canonicalLink = ensureLink('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    });
    canonicalLink.setAttribute('href', `https://ttstats.ch${canonicalPath}`);

    // Only add hreflang alternates for language-prefixed routes.
    const parts = canonicalPath.split('/').filter(Boolean);
    const isLangRoute = parts.length > 0 && ['en', 'de', 'fr', 'it'].includes(parts[0]);
    const altLangs: Array<'en' | 'de' | 'fr' | 'it'> = ['en', 'de', 'fr', 'it'];

    // Clean old alternates we previously injected.
    document.querySelectorAll('link[data-ttstats-hreflang="true"]').forEach(n => n.remove());
    if (isLangRoute) {
      const rest = parts.slice(1).join('/');
      altLangs.forEach(alt => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'alternate');
        l.setAttribute('hreflang', alt);
        l.setAttribute('href', `https://ttstats.ch/${alt}${rest ? `/${rest}` : ''}`);
        l.setAttribute('data-ttstats-hreflang', 'true');
        document.head.appendChild(l);
      });
      const x = document.createElement('link');
      x.setAttribute('rel', 'alternate');
      x.setAttribute('hreflang', 'x-default');
      x.setAttribute('href', `https://ttstats.ch/en${rest ? `/${rest}` : ''}`);
      x.setAttribute('data-ttstats-hreflang', 'true');
      document.head.appendChild(x);
    }

    // Add structured data (JSON-LD)
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    const fullUrl = `https://ttstats.ch${canonicalPath}`;
    const breadcrumbNode =
      breadcrumbs && breadcrumbs.length > 0
        ? {
            '@type': 'BreadcrumbList',
            '@id': `${fullUrl}#breadcrumb`,
            itemListElement: breadcrumbs.map((b, idx) => ({
              '@type': 'ListItem',
              position: idx + 1,
              name: b.name,
              item: `https://ttstats.ch${b.path}`
            }))
          }
        : null;

    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://ttstats.ch/#organization',
          name: 'TTStats',
          url: 'https://ttstats.ch',
          logo: 'https://ttstats.ch/logo512.png',
          description,
          sameAs: [
            'https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609',
            'https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats'
          ]
        },
        {
          '@type': 'WebSite',
          '@id': 'https://ttstats.ch/#website',
          url: 'https://ttstats.ch',
          name: 'TTStats',
          description,
          publisher: {
            '@id': 'https://ttstats.ch/#organization'
          },
          inLanguage: lang
        },
        {
          '@type': 'WebPage',
          '@id': `${fullUrl}#webpage`,
          url: fullUrl,
          name: title,
          description,
          isPartOf: { '@id': 'https://ttstats.ch/#website' },
          inLanguage: lang
        },
        ...(breadcrumbNode ? [breadcrumbNode] : []),
        ...extraGraphNodes
      ]
    };

    const script = document.createElement('script');
    script.id = 'structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

  }, [
    lang,
    location.pathname,
    titleOverride,
    descriptionOverride,
    keywordsOverride,
    ogTitleOverride,
    ogDescriptionOverride,
    ogImageOverride,
    canonicalPathOverride,
    noindex,
    breadcrumbs,
    extraStructuredData
  ]);

  return null; // This component doesn't render anything
};

export default SEOHead;
